import { ZeusHelpButton } from "@/components/zeus-help-dialog";
import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AutoExpandingTextarea } from "@/components/auto-expanding-textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Trash2, GripVertical, Plus, AlertCircle } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Intention, NewIntention } from "@shared/schema";

interface SortableIntentionItemProps {
  intention: Intention;
  index: number;
  onTextChange: (id: string, text: string) => void;
  onDelete: (id: string) => void;
  onTextBlur: (id: string, text: string) => void;
}

function SortableIntentionItem({ 
  intention, 
  index, 
  onTextChange, 
  onDelete,
  onTextBlur 
}: SortableIntentionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: intention.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onTextChange(intention.id, e.target.value);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 sm:gap-4 p-2 sm:p-4 bg-card border rounded-lg"
      data-testid={`intention-item-${intention.id}`}
    >
      <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs sm:text-sm font-medium mt-1">
        {index + 1}
      </div>

      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 sm:p-1 hover:bg-muted rounded mt-1"
        data-testid={`drag-handle-${intention.id}`}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>

      <AutoExpandingTextarea
        value={intention.text}
        onChange={handleChange}
        onBlur={(e) => onTextBlur(intention.id, e.target.value)}
        placeholder="Enter your intention..."
        className="flex-1 min-w-0 min-h-[40px]"
        minRows={1}
        maxRows={10}
        data-testid={`input-intention-${intention.id}`}
      />

      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete(intention.id)}
        className="flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10 mt-1 p-1 sm:p-2"
        data-testid={`button-delete-${intention.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

export default function IntentionsPage() {
  const [localIntentions, setLocalIntentions] = useState<Intention[]>([]);
  const prevIntentionsRef = useRef<Intention[]>([]);

  // Fetch intentions
  const { data: intentions = [], isLoading, error } = useQuery<Intention[]>({
    queryKey: ["/api/intentions"],
  });

  // Sync local state with server data only when data actually changes
  useEffect(() => {
    const prevIntentions = prevIntentionsRef.current;
    const hasChanged = 
      intentions.length !== prevIntentions.length ||
      intentions.some((intent, idx) => 
        !prevIntentions[idx] || 
        intent.id !== prevIntentions[idx].id ||
        intent.text !== prevIntentions[idx].text ||
        intent.sortOrder !== prevIntentions[idx].sortOrder
      );
    
    if (hasChanged) {
      setLocalIntentions(intentions);
      prevIntentionsRef.current = intentions;
    }
  }, [intentions]);

  // Create intention mutation
  const createMutation = useMutation({
    mutationFn: (data: NewIntention) => apiRequest("POST", "/api/intentions", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/intentions"] });
    },
    onError: () => {
    },
  });

  // Update intention mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Intention> }) =>
      apiRequest("PATCH", `/api/intentions/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/intentions"] });
    },
    onError: () => {
      // Revert local state
      setLocalIntentions(intentions);
    },
  });

  // Delete intention mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/intentions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/intentions"] });
    },
    onError: () => {
      // Revert local state
      setLocalIntentions(intentions);
    },
  });

  // Reorder intentions mutation
  const reorderMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      apiRequest("POST", "/api/intentions/reorder", { orderedIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/intentions"] });
    },
    onError: () => {
      // Revert local state
      setLocalIntentions(intentions);
    },
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle text changes (optimistic update)
  const handleTextChange = useCallback((id: string, text: string) => {
    setLocalIntentions(prev => 
      prev.map(intention => 
        intention.id === id ? { ...intention, text } : intention
      )
    );
  }, []);

  // Handle text blur (save changes)
  const handleTextBlur = useCallback((id: string, text: string) => {
    const original = intentions.find(i => i.id === id);
    if (original && original.text !== text) {
      updateMutation.mutate({ id, data: { text } });
    }
  }, [intentions, updateMutation]);

  // Handle drag end
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localIntentions.findIndex(item => item.id === active.id);
      const newIndex = localIntentions.findIndex(item => item.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(localIntentions, oldIndex, newIndex);
        setLocalIntentions(newOrder);
        
        // Send reorder request
        const orderedIds = newOrder.map(intention => intention.id);
        reorderMutation.mutate(orderedIds);
      }
    }
  }, [localIntentions, reorderMutation]);

  // Handle add intention
  const handleAddIntention = useCallback(() => {
    createMutation.mutate({ text: "" });
  }, [createMutation]);

  // Handle delete intention
  const handleDeleteIntention = useCallback((id: string) => {
    // Optimistic update
    setLocalIntentions(prev => prev.filter(intention => intention.id !== id));
    deleteMutation.mutate(id);
  }, [deleteMutation]);

  if (isLoading) {
    return (
      <div className="w-full max-w-full p-4 md:p-6">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Affirmations</h1>
            <p className="text-muted-foreground mt-1">
              Set and prioritize your daily affirmations. Enter what your affirmations are for the day, or enter positive affirmations.
            </p>
          </div>
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="w-8 h-8 rounded-full" />
                    <Skeleton className="w-6 h-6" />
                    <Skeleton className="flex-1 h-10" />
                    <Skeleton className="w-8 h-8" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-full p-4 md:p-6">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Affirmations</h1>
            <p className="text-muted-foreground mt-1">
              Set and prioritize your daily affirmations. Enter what your affirmations are for the day, or enter positive affirmations.
            </p>
          </div>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load affirmations. Please refresh the page or try again later.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full p-4 md:p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground" data-testid="page-title">
              Affirmations
            </h1>
            <p className="text-muted-foreground mt-1">
              Set and prioritize your daily affirmations. Enter what your affirmations are for the day, or enter positive affirmations. Drag to reorder by priority.
            </p>
          </div>
          <ZeusHelpButton focus="affirmations" />
        </div>

        {/* Affirmations List */}
        <Card>
          <CardHeader>
            <CardTitle>Your Affirmations</CardTitle>
            <CardDescription>
              Organize your affirmations in order of priority. Your most important affirmations should be at the top.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {localIntentions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  You haven't set any affirmations yet. Start by adding your first affirmation.
                </p>
                <Button onClick={handleAddIntention} data-testid="button-add-first-intention">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Affirmation
                </Button>
              </div>
            ) : (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={localIntentions.map(i => i.id)} 
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3" data-testid="intentions-list">
                      {localIntentions.map((intention, index) => (
                        <SortableIntentionItem
                          key={intention.id}
                          intention={intention}
                          index={index}
                          onTextChange={handleTextChange}
                          onDelete={handleDeleteIntention}
                          onTextBlur={handleTextBlur}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                <div className="pt-4 border-t">
                  <Button 
                    onClick={handleAddIntention}
                    variant="outline"
                    className="w-full"
                    disabled={createMutation.isPending}
                    data-testid="button-add-intention"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {createMutation.isPending ? "Adding..." : "Add Another Affirmation"}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Tips */}
        <Card>
          <CardHeader>
            <CardTitle>Tips for Effective Affirmations</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Keep affirmations clear and specific</li>
              <li>• Focus on how you want to feel or be, not just what you want to do</li>
              <li>• Prioritize your most important affirmations at the top</li>
              <li>• Review and adjust your affirmations regularly</li>
              <li>• Use drag and drop to reorder by priority</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}